import { APP_INITIALIZER, ErrorHandler, Injector, NgModule } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy, Router } from '@angular/router';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { ServiceWorkerModule } from '@angular/service-worker';
import { MatFormFieldDefaultOptions, MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBarModule } from '@angular/material/snack-bar';

import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { IonicStorageModule } from '@ionic/storage-angular';

import { AhsTranslationOptionsService, BaseTranslateModule } from '@nx-ahs/base-translate';
import { TranslateModule } from '@ngx-translate/core';
import { DynamicScriptingOptionsService, DynamicScriptingService } from '@nx-ahs/script-shared/services';
import { ScriptActionsModalModule } from './modules/script-actions/script-actions.module';

import { BehaviorSubject, firstValueFrom } from 'rxjs';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';

import { ThemeService } from './core/ion-ahs-theme/theme.service';

import { FontAwesomeModule, FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { fass } from '@awesome.me/kit-bde9a98016/icons';

import { FaMatIconRegistryService } from '@nx-ahs/shared/services';
import { SiteStorageKeys } from './shared/constants/site-storage-keys';
import { AppCoreService } from './core/services/app-core.service';
import { AppHttpInterceptor } from './core/interceptors/http-interceptor.service';
import { SecurityService } from './core/services/security.service';
import { IonAhsDialogueModule } from './core/ion-ahs-dialogue/ion-ahs-dialogue/ion-ahs-dialogue.module';
import { environment } from '../environments/environment';
import { TutorialComponent } from './modules/help/components/tutorial/tutorial.component';
import { IntroComponent } from './modules/help/components/intro/intro.component';
import { PreSplashModule } from './modules/pre-splash/pre-splash.module';
import { AhsSplashModule } from './modules/ahs-splash/ahs-splash.module';
import { InsertPersonModule } from './modules/insert-person/insert-person.module';
import { PrimaryPersonSearchModule } from './modules/my-members/primary-person-search/primary-person-search.module';
import { AddPrimaryPersonModule } from './modules/my-members/add-primary-person/add-primary-person.module';
import { EntityCardDetailsModule } from './modules/entity-card-details/entity-card-details.module';
import { PdfViewerModalComponent } from './modules/pdf-viewer-modal/pdf-viewer-modal.component';

import { IonicSelectableComponent } from 'ionic-selectable';
import { AccountService } from './core/services/account.service';
import { FoundMembersComponent } from './modules/my-members/found-members/found-members.component';
import { NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { LayoutModule } from './shared/components/layout/layout.module';
import { CustomSearchComponent } from './modules/tripetto-components/custom-search/custom-search.component';
import { TripettoComponentsModule } from './modules/tripetto-components/tripetto-components.module';

import { ButtonOpenComponentModalComponent } from './modules/tripetto-components/button-open-component-modal/button-open-component-modal.component';
import { LabelListComponent } from './modules/tripetto-components/label-list/label-list.component';
import { KeepPlanComponent } from './modules/enrollment/keep-plan/keep-plan.component';
import { BenefitsChangeComponent } from './modules/enrollment/benefits-change/benefits-change.component';
import { MaintenancePageModule } from './modules/security/maintenance/maintenance.module';
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { StatusBar } from '@awesome-cordova-plugins/status-bar/ngx';
import { SplashScreen } from '@awesome-cordova-plugins/splash-screen/ngx';
import { MailDialogService, MailService } from '@nx-ahs/mail';
import { TIMEOUT_DIALOG_COMPONENT, TimeoutComponent, TimeoutModule } from '@nx-ahs/timeout';
import {
	TRANSLATION_CREATION_DIALOG_COMPONENT,
	ClientTranslationCreateNewRecordComponent
} from '@nx-ahs/translation-manager';
import { BrowserNotificationService } from './core/services/browser-notification.service';
import { GlobalErrorHandler } from './global-error-handler.service';
import { FormlyModule } from '@ngx-formly/core';
import { AhsFormlySharedModule } from '@nx-ahs/formly/shared';
import { MatDialogModule } from '@angular/material/dialog';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FeatureFlagService } from '@nx-ahs/feature-flags';
import { RecaptchaComponent } from './modules/security/recaptcha/recaptcha.component';
import { RecaptchaComponentModule } from './modules/security/recaptcha/recaptcha.module';

const appearance: MatFormFieldDefaultOptions = {
	appearance: 'outline'
};

@NgModule({
	declarations: [AppComponent, IntroComponent, TutorialComponent, PdfViewerModalComponent],
	schemas: [CUSTOM_ELEMENTS_SCHEMA],
	exports: [IntroComponent, TutorialComponent],
	imports: [
		TranslateModule.forRoot(),
		BaseTranslateModule.forRoot({
			// TODO: this must be in config
			languages: [
				{
					name: 'English',
					code: 'en',
					isDefault: true,
					id: 1
				},
				{
					name: 'Espanol',
					code: 'es',
					isDefault: false,
					id: 2
				},
				{
					name: 'Chinese',
					code: 'zh',
					isDefault: false,
					id: 3
				},
				{
					name: 'Vietnamese',
					code: 'vi',
					isDefault: false,
					id: 4
				},
				{
					name: 'Somali',
					code: 'so',
					isDefault: false,
					id: 5
				}
			],
			siteLanguageStorageKey: SiteStorageKeys.siteLanguageKey
		}),
		BrowserModule,
		MatDialogModule,
		BrowserAnimationsModule,
		IonicStorageModule.forRoot(),
		FormlyModule.forRoot(),
		AhsFormlySharedModule,
		AppRoutingModule,
		FlexLayoutModule,
		FormsModule,
		ReactiveFormsModule,
		HttpClientModule,
		FontAwesomeModule,
		IonicSelectableComponent,

		// this removes the verbiage "back" text on the ion-back --button through out the app
		// can add other app wide stuff here for ionic
		// https://ionicframework.com/docs/v3/api/config/Config/
		IonicModule.forRoot({
			backButtonText: '',
			animated: false
		}),
		MatTooltipModule,
		MatInputModule,
		MatSnackBarModule,
		PreSplashModule,
		AhsSplashModule,
		IonAhsDialogueModule,
		InsertPersonModule,
		PrimaryPersonSearchModule,
		AddPrimaryPersonModule,
		EntityCardDetailsModule,
		ScriptActionsModalModule,
		ServiceWorkerModule.register('ngsw-worker.js', {
			enabled: true,
			// Register the ServiceWorker as soon as the app is stable
			// or after 30 seconds (whichever comes first).
			registrationStrategy: 'registerWhenStable:30000'
		}),
		LayoutModule,
		TripettoComponentsModule,
		MaintenancePageModule,
		TimeoutModule,
		RecaptchaComponentModule
	],
	providers: [
		CurrencyPipe,
		DatePipe,
		StatusBar,
		SplashScreen,
		provideNgxMask(),
		NgxMaskPipe,
		{ provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
		// Here we request that configuration loading be done at app-
		// initialization time (prior to rendering)
		{
			provide: HTTP_INTERCEPTORS,
			useClass: AppHttpInterceptor,
			multi: true
		},
		{
			provide: APP_INITIALIZER,
			useFactory: appInitFactory,
			deps: [
				AppCoreService,
				SecurityService,
				AhsTranslationOptionsService,
				ThemeService,
				DynamicScriptingService,
				Injector,
				FeatureFlagService
			],
			multi: true
		},
		{
			provide: 'environment',
			useValue: environment
		},
		{
			provide: 'appCoreService',
			useExisting: AppCoreService
		},
		{
			provide: 'clientConfig',
			useValue: new BehaviorSubject<any>({})
		},
		{
			provide: 'securityService',
			useFactory: (securityService: SecurityService) => {
				if (!(securityService as any).getCurrentApp) {
					(securityService as any).getCurrentApp = () => {
						return {
							permissions: []
						};
					};
				}

				return securityService;
			},
			deps: [SecurityService]
		},
		{
			provide: 'accountService',
			useFactory: (accountService: AccountService) => {
				return accountService;
			},
			deps: [AccountService]
		},
		{
			provide: 'mailService',
			useFactory: (mailService: MailService) => {
				return mailService;
			},
			deps: [MailService]
		},
		{
			provide: 'mailDialogService',
			useFactory: (mailDialogService: MailDialogService) => {
				return mailDialogService;
			},
			deps: [MailDialogService]
		},
		{
			provide: 'browserNotificationService',
			useFactory: (browserNotificationService: BrowserNotificationService) => {
				return browserNotificationService;
			},
			deps: [BrowserNotificationService]
		},
		{
			provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
			useValue: appearance
		},
		{
			provide: ErrorHandler,
			useClass: GlobalErrorHandler
		},
		{
			provide: 'featureFlagService',
			useExisting: FeatureFlagService
		},
		{ provide: TIMEOUT_DIALOG_COMPONENT, useValue: TimeoutComponent },
		{ provide: TRANSLATION_CREATION_DIALOG_COMPONENT, useValue: ClientTranslationCreateNewRecordComponent }
	],
	bootstrap: [AppComponent]
})
export class AppModule {
	constructor(
		private library: FaIconLibrary,
		private faMatIconRegistry: FaMatIconRegistryService,
		private dynamicScriptingService: DynamicScriptingService
	) {
		// Load custom FontAwesome kit icons and register them as mat-icons
		library.addIconPacks(fass as any);
		this.faMatIconRegistry.registerAll();

		// Add custom script components
		this.dynamicScriptingService.addCustomComponent('FoundMembersComponent', FoundMembersComponent);
		this.dynamicScriptingService.addCustomComponent('CustomSearchComponent', CustomSearchComponent);
		this.dynamicScriptingService.addCustomComponent(
			'ButtonOpenComponentModalComponent',
			ButtonOpenComponentModalComponent
		);
		this.dynamicScriptingService.addCustomComponent('LabelListComponent', LabelListComponent);
		this.dynamicScriptingService.addCustomComponent('KeepPlanComponent', KeepPlanComponent);
		this.dynamicScriptingService.addCustomComponent('BenefitsChangeComponent', BenefitsChangeComponent);
		this.dynamicScriptingService.addCustomComponent('RecaptchaComponent', RecaptchaComponent);
	}
}

export function appInitFactory(
	appCoreService: AppCoreService,
	securityService: SecurityService,
	ahsTranslateOptions: AhsTranslationOptionsService,
	themeService: ThemeService,
	dynamicScriptingService: DynamicScriptingService,
	injector: Injector,
	featureFlagService: FeatureFlagService
) {
	return () =>
		appCoreService
			.isAPILive()
			.then(async (status) => {
				if (!status.result) {
					appCoreService.isDBReady = false;
				}

				await appCoreService.init();
				// Setup Scripting Service
				const model = new DynamicScriptingOptionsService();
				model.apiUrl = environment.apiUrl;
				model.localTokenName = 'adal.idtoken';
				model.injector = injector;
				model.clientConfig = appCoreService.clientConfig;
				model.x_ht_appId = appCoreService?.currentApplication?.appId?.toString();

				// Load feature flags after all other initialization is complete
				await firstValueFrom(featureFlagService.getFeatureFlags());

				dynamicScriptingService.init(model);
				securityService.initialize();
				ahsTranslateOptions.clientConfig.languages = appCoreService.clientConfig.languages;
				//bind to dynamic manifest for PWA
				const stringManifest = JSON.stringify(appCoreService.clientConfig.webmanifest);
				const blob = new Blob([stringManifest], { type: 'application/json' });
				const manifestURL = URL.createObjectURL(blob);
				const dom = document.querySelector('#my-manifest-placeholder');
				dom.setAttribute('href', manifestURL);
				themeService.createIonColorPallete(appCoreService.clientConfig.theme);
				themeService.setGlobalVariablesFromConfig(appCoreService.clientConfig.globalCssVariables);

				if (appCoreService.clientConfig.fonts)
					themeService.setGlobalFontsFromConfig(appCoreService.clientConfig.fonts);
			})
			.catch((errors) => {
				appCoreService.isDBReady = false;
				appCoreService.isLive = false;
				console.log(errors.message);
			});
}
